import { Fragment, ReactNode, type ComponentPropsWithoutRef } from 'react';
import { twMerge } from 'tailwind-merge';

import * as Tooltip from '@radix-ui/react-tooltip';
import type { AvomaTooltipPosition } from './AvomaTooltip';

type TTooltipSide = 'top' | 'right' | 'bottom' | 'left';
type TTooltipAlign = 'start' | 'center' | 'end';

interface ITooltipPlacement {
  side: TTooltipSide;
  align: TTooltipAlign;
  contentClassName?: string;
}

const LEGACY_POSITION_PLACEMENTS: Record<
  AvomaTooltipPosition,
  ITooltipPlacement
> = {
  top: { side: 'top', align: 'start' },
  'top-left': { side: 'top', align: 'start' },
  'top-center': { side: 'top', align: 'center' },
  'top-right': { side: 'top', align: 'end' },
  right: { side: 'right', align: 'start' },
  'right-top': { side: 'right', align: 'start' },
  'right-center': { side: 'right', align: 'center' },
  'right-bottom': { side: 'right', align: 'end' },
  bottom: { side: 'bottom', align: 'start' },
  'bottom-left': { side: 'bottom', align: 'start' },
  'bottom-center': { side: 'bottom', align: 'center' },
  'bottom-right': { side: 'bottom', align: 'end' },
  left: { side: 'left', align: 'start' },
  'left-top': { side: 'left', align: 'start' },
  'left-center': { side: 'left', align: 'center' },
  'left-bottom': { side: 'left', align: 'end' }
};

const LEGACY_WRAPPED_CONTENT_CLASSES: Record<string, string> = {
  wrapped: 'w-max max-w-[400px] whitespace-normal',
  wrap: 'w-max max-w-[400px] whitespace-normal',
  'wrapped-small': 'w-max max-w-[200px] whitespace-normal',
  'wrapped-tight': 'w-max max-w-[160px] whitespace-normal'
};

export const mapAvomaTooltipPositionToRadixPlacement = (
  tooltipPosition?: AvomaTooltipPosition | string
): ITooltipPlacement | undefined => {
  if (!tooltipPosition) {
    return undefined;
  }

  const tokens = tooltipPosition.split(/\s+/).filter(Boolean);
  const placementToken = tokens.find(
    (token): token is AvomaTooltipPosition =>
      token in LEGACY_POSITION_PLACEMENTS
  );

  if (!placementToken) {
    return undefined;
  }

  const wrappedClasses = tokens
    .map(token => LEGACY_WRAPPED_CONTENT_CLASSES[token])
    .filter(Boolean)
    .join(' ');

  return {
    ...LEGACY_POSITION_PLACEMENTS[placementToken],
    contentClassName: wrappedClasses || undefined
  };
};

/**
 * AvomaTooltipV2 - Enhanced tooltip component built on Radix UI
 *
 * This component provides a more robust and accessible tooltip implementation
 * compared to the original AvomaTooltip component. Key improvements include:
 *
 * - Built on Radix UI's tooltip primitives for better accessibility
 * - Automatic collision detection and positioning
 * - Better keyboard navigation support
 * - Improved screen reader compatibility
 * - Configurable alignment and positioning
 *
 *
 * @param children - The element that triggers the tooltip
 * @param tooltip - The tooltip text to display (optional - renders children only if not provided)
 * @param align - Horizontal alignment: 'start', 'center', or 'end'
 * @param side - Vertical position: 'top' or 'bottom'
 * @param tooltipPosition - Legacy AvomaTooltip position string used during migrations
 */
interface IAvomaTooltipV2Props {
  children: ReactNode;
  tooltip?: string | ReactNode;
  align?: TTooltipAlign;
  side?: TTooltipSide;
  sideOffset?: number;
  tooltipPosition?: AvomaTooltipPosition | string;
  collisionPadding?: number;
  /** Optional class name for the tooltip content (e.g. to set width: "max-w-[240px]" or "w-64") */
  contentClassName?: string;
  contentProps?: ComponentPropsWithoutRef<typeof Tooltip.Content>;
  arrowClassName?: string;
  arrowProps?: ComponentPropsWithoutRef<typeof Tooltip.Arrow>;
  /** Pass false to forcefully hide the tooltip (e.g. while the trigger element is focused/editing). Omit to let Radix manage open state internally. */
  open?: boolean;
}

const DEFAULT_CONTENT_CLASS =
  'bg-gunmetal text-white font-bold rounded-md px-2 py-1 text-xs max-w-[168px] whitespace-wrap select-none shadow-lg z-[999999]';
const DEFAULT_ARROW_CLASS =
  'fill-gunmetal -mt-px origin-center scale-125';

const AvomaTooltipV2 = ({
  children,
  tooltip,
  align = 'center',
  side = 'top',
  sideOffset = 5,
  tooltipPosition,
  collisionPadding = 10,
  contentClassName,
  contentProps,
  arrowClassName,
  arrowProps,
  open
}: IAvomaTooltipV2Props) => {
  // If no tooltip is provided, just render the children
  if (!tooltip) {
    return <Fragment>{children}</Fragment>;
  }

  const legacyPlacement =
    mapAvomaTooltipPositionToRadixPlacement(tooltipPosition);
  const resolvedSide = legacyPlacement?.side ?? side;
  const resolvedAlign = legacyPlacement?.align ?? align;
  const { style: contentStyle, ...restContentProps } = contentProps ?? {};
  const { style: arrowStyle, ...restArrowProps } = arrowProps ?? {};

  return (
    <Tooltip.Provider>
      <Tooltip.Root delayDuration={300} open={open}>
        <Tooltip.Trigger asChild>{children}</Tooltip.Trigger>
        <Tooltip.Portal>
          <Tooltip.Content
            {...restContentProps}
            className={twMerge(
              DEFAULT_CONTENT_CLASS,
              legacyPlacement?.contentClassName,
              contentClassName
            )}
            sideOffset={sideOffset}
            collisionPadding={collisionPadding}
            align={resolvedAlign}
            side={resolvedSide}
            style={contentStyle}
          >
            {tooltip}
            <Tooltip.Arrow
              {...restArrowProps}
              width={14}
              height={7}
              className={twMerge(DEFAULT_ARROW_CLASS, arrowClassName)}
              style={arrowStyle}
            />
          </Tooltip.Content>
        </Tooltip.Portal>
      </Tooltip.Root>
    </Tooltip.Provider>
  );
};

export default AvomaTooltipV2;
